/**
 * Plataforma (Control Plane / cockpit superior, B-34 F4) — SÓ STAFF.
 * Camada ACIMA dos tenants: visão-geral de governança, clientes 360º +
 * provisionamento/entitlements/marcas, ANALYTICS cruzado de todos os clientes
 * (ativo da casa) e consumo de IA por cliente. Tenant → 404 do backend.
 */
import { useMemo, useState } from 'react';

import { Badge, Button } from '@/components/atoms';
import { formatarNumero, formatarDataHora } from '@/i18n/formato';
import { ApiError } from '@/services/api';
import { JANELAS, useFunilResumo, type Janela } from '@/services/funilEventos';
import {
  type PlatformClient,
  usePackages,
  useAccessInfo,
  useAssignSite,
  useCreateClient,
  useDeprovisionClient,
  useExportClient,
  usePurgeClient,
  useFeatureUsage,
  usePlatformAnalytics,
  usePlatformBilling,
  usePlatformClients,
  usePlatformOverview,
  usePlatformSerp,
  usePlatformUsage,
  usePlatformVigias,
  useResetAdmin,
  useUpdateClient,
} from '@/services/platform';

import styles from './Platform.module.css';

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

function usd(n: number): string {
  return `$${n.toFixed(n >= 100 ? 2 : 4)}`;
}

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

const EMPTY_FORM = {
  slug: '',
  name: '',
  pkg: 'enterprise',
  ai_mode: 'pool',
  admin_email: '',
  admin_nome: '',
};

type Tab =
  | 'overview'
  | 'clients'
  | 'funil'
  | 'analytics'
  | 'usage'
  | 'features'
  | 'serp'
  | 'vigias';
const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Visão geral' },
  { key: 'clients', label: 'Clientes' },
  // L4.3 (pos-dd-iv): os números de `evento_funil`, que até aqui só existiam
  // no banco. Aba nova nesta tela, e não painel novo: o funil de receita é
  // pergunta da CASA, e a casa já tem um cockpit — inventar um segundo lugar
  // para olhar seria criar o dashboard que ninguém abre.
  { key: 'funil', label: 'Funil' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'usage', label: 'Consumo' },
  { key: 'features', label: 'Uso de features' },
  { key: 'serp', label: 'SERP' },
  { key: 'vigias', label: 'Vigias' },
];

/** Estados da conta com nome e tom proprios — `resolve_operational` no backend
 * considera active|trialing|past_due OPERACIONAIS; o resto bloqueia. */
const ROTULO_STATUS: Record<string, string> = {
  active: 'ativo',
  trialing: 'trial',
  past_due: 'pagamento pendente',
  suspended: 'suspenso',
  deleting: 'em exclusão',
  churned: 'encerrado',
};
const TOM_STATUS: Record<string, 'ok' | 'info' | 'warn' | 'crit' | 'neutral'> = {
  active: 'ok',
  trialing: 'info',
  past_due: 'warn',
  suspended: 'crit',
  deleting: 'crit',
  churned: 'neutral',
};
const OPERACIONAIS = new Set(['active', 'trialing', 'past_due']);

export function Platform() {
  const clientsQ = usePlatformClients();
  const overviewQ = usePlatformOverview();
  const analyticsQ = usePlatformAnalytics();
  const usageQ = usePlatformUsage(6);
  const billingQ = usePlatformBilling();
  const packagesQ = usePackages();
  const accessInfoQ = useAccessInfo();
  const serpQ = usePlatformSerp();
  const vigiasQ = usePlatformVigias();
  const [usageDays, setUsageDays] = useState<30 | 90>(30);
  const featureUsageQ = useFeatureUsage(usageDays);
  const [funilDias, setFunilDias] = useState<Janela>(30);
  const funilQ = useFunilResumo(funilDias);
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const assignSite = useAssignSite();
  const resetAdmin = useResetAdmin();
  const exportClient = useExportClient();
  const deprovision = useDeprovisionClient();
  const purge = usePurgeClient();

  const [tab, setTab] = useState<Tab>('overview');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [created, setCreated] = useState<{
    slug: string;
    admin_email?: string;
    temp_password?: string;
  } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [assignFor, setAssignFor] = useState<PlatformClient | null>(null);
  const [assignSlug, setAssignSlug] = useState('');
  const [featuresFor, setFeaturesFor] = useState<PlatformClient | null>(null);
  const [featuresText, setFeaturesText] = useState('');
  const [planFor, setPlanFor] = useState<PlatformClient | null>(null);
  const [planPkg, setPlanPkg] = useState('');
  const [offboardFor, setOffboardFor] = useState<PlatformClient | null>(null);
  const [offboardConfirm, setOffboardConfirm] = useState('');

  const clients = clientsQ.data ?? [];
  const overview = overviewQ.data;

  const usageMonths = useMemo(() => {
    const set = new Set<string>();
    for (const c of usageQ.data?.by_client ?? []) {
      for (const m of Object.keys(c.points)) set.add(m);
    }
    return [...set].sort();
  }, [usageQ.data]);

  if (clientsQ.isError) {
    return (
      <section className={styles.root}>
        <h1 className={styles.title}>Plataforma</h1>
        <p className={styles.muted}>
          Área restrita ao staff da plataforma — não disponível para esta conta.
        </p>
      </section>
    );
  }

  async function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      const out = await createClient.mutateAsync({
        slug: form.slug.trim().toLowerCase(),
        name: form.name.trim(),
        package: form.pkg,
        plan: 'standalone', // ignorado quando package vem (backend resolve pelo pacote)
        ai_mode: form.ai_mode,
        ...(form.admin_email.trim()
          ? { admin_email: form.admin_email.trim(), admin_nome: form.admin_nome.trim() }
          : {}),
      });
      setCreated(out);
      setCreating(false);
      setForm(EMPTY_FORM);
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  const appUrl =
    accessInfoQ.data?.app_url || (typeof window !== 'undefined' ? window.location.origin : '');

  async function copy(label: string, value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      setMsg('Não foi possível copiar (permissão do navegador).');
    }
  }

  async function resetOne(c: PlatformClient) {
    setMsg(null);
    try {
      const out = await resetAdmin.mutateAsync(c.id);
      setCreated({
        slug: out.slug,
        admin_email: out.admin_email,
        temp_password: out.temp_password,
      });
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function setHost(c: PlatformClient) {
    const host = window.prompt(
      `Domínio dedicado de ${c.name} (vazio = host compartilhado).\n` +
        `Só registra — o roteamento DNS/Traefik é manual (ver runbook).`,
      c.host ?? '',
    );
    if (host === null) return;
    setMsg(null);
    try {
      await updateClient.mutateAsync({
        id: c.id,
        payload: { host: host.trim() || null } as Partial<PlatformClient>,
      });
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function toggleStatus(c: PlatformClient) {
    setMsg(null);
    try {
      // Toggle antigo era binario sobre 'active': para um cliente TRIALING,
      // "suspender" gravava... 'active'. A direcao agora vem do estado real:
      // quem opera (active/trialing/past_due) vai para suspended; suspended
      // volta para active (e SO suspended — trial nao se "reativa" para
      // active, senao perde a natureza de trial).
      await updateClient.mutateAsync({
        id: c.id,
        payload: { status: OPERACIONAIS.has(c.status) ? 'suspended' : 'active' },
      });
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function submitAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!assignFor) return;
    setMsg(null);
    try {
      await assignSite.mutateAsync({ id: assignFor.id, site_slug: assignSlug.trim() });
      setAssignFor(null);
      setAssignSlug('');
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  function openFeatures(c: PlatformClient) {
    setFeaturesFor(c);
    setFeaturesText(JSON.stringify(c.features ?? {}, null, 2));
    setMsg(null);
  }

  async function doExport(c: PlatformClient) {
    setMsg(null);
    try {
      const data = await exportClient.mutateAsync(c.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export-${c.slug}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function doDeprovision(mode: 'suspend' | 'soft_delete') {
    if (!offboardFor) return;
    setMsg(null);
    try {
      await deprovision.mutateAsync({ id: offboardFor.id, confirm_slug: offboardConfirm, mode });
      setOffboardFor(null);
      setOffboardConfirm('');
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function doPurge() {
    if (!offboardFor) return;
    setMsg(null);
    try {
      await purge.mutateAsync({ id: offboardFor.id, confirm_slug: offboardConfirm });
      setOffboardFor(null);
      setOffboardConfirm('');
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function submitPlan(e: React.FormEvent) {
    e.preventDefault();
    if (!planFor) return;
    setMsg(null);
    try {
      await updateClient.mutateAsync({ id: planFor.id, payload: { package: planPkg } });
      setPlanFor(null);
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  async function submitFeatures(e: React.FormEvent) {
    e.preventDefault();
    if (!featuresFor) return;
    setMsg(null);
    try {
      const parsed = JSON.parse(featuresText || '{}');
      await updateClient.mutateAsync({ id: featuresFor.id, payload: { features: parsed } });
      setFeaturesFor(null);
    } catch (err) {
      setMsg(err instanceof SyntaxError ? 'JSON inválido.' : `Erro: ${describeError(err)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Plataforma</h1>
          <p className={styles.subtitle}>
            Control plane — governança acima dos tenants. Ações de staff são auditadas.
          </p>
        </div>
        {tab === 'clients' && (
          <Button
            onClick={() => {
              setCreating(true);
              setMsg(null);
            }}
          >
            Novo cliente
          </Button>
        )}
      </header>

      <nav className={styles.tabs}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {msg && <p className={styles.flash}>{msg}</p>}

      {/* ─── Visão geral ─────────────────────────────────────────────── */}
      {tab === 'overview' && (
        <div>
          <div className={styles.kpis}>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Clientes</span>
              <span className={styles.kpiValue}>{overview?.clients.total ?? '—'}</span>
              <span className={styles.kpiHint}>
                {overview
                  ? `${overview.clients.enterprise} enterprise · ${overview.clients.standalone} standalone`
                  : ''}
              </span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Marcas</span>
              <span className={styles.kpiValue}>{overview?.brands ?? '—'}</span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Usuários</span>
              <span className={styles.kpiValue}>{overview?.users.total ?? '—'}</span>
              <span className={styles.kpiHint}>
                {overview ? `${overview.users.staff} staff · ${overview.users.tenant} tenant` : ''}
              </span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Conteúdo</span>
              <span className={styles.kpiValue}>
                {overview ? compact(overview.content.pautas) : '—'}
              </span>
              <span className={styles.kpiHint}>
                {overview ? `${compact(overview.content.publicadas)} publicadas` : ''}
              </span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Gasto IA (mês)</span>
              <span className={styles.kpiValue}>
                {overview ? usd(overview.spend.month_usd) : '—'}
              </span>
              <span className={styles.kpiHint}>
                {overview ? `${usd(overview.spend.total_usd)} total` : ''}
              </span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>MRR</span>
              <span className={styles.kpiValue}>
                {overview ? `R$ ${formatarNumero(overview.mrr_brl)}` : '—'}
              </span>
              <span className={styles.kpiHint}>assinaturas ativas</span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Suspensos</span>
              <span className={styles.kpiValue}>{overview?.clients.suspended ?? '—'}</span>
            </div>
          </div>
          <p className={styles.note}>
            Estes números cruzam todos os tenants — o ativo da casa. Detalhe por cliente nas abas
            Clientes, Analytics e Consumo.
          </p>
        </div>
      )}

      {/* ─── Clientes ────────────────────────────────────────────────── */}
      {tab === 'clients' && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Plano</th>
              <th>IA</th>
              <th>Marcas</th>
              <th>Usuários</th>
              <th>Gasto (mês)</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr
                key={c.id}
                className={
                  /* Apagar a linha (opacity .55) quer dizer "nao opera", nao
                     "nao esta em active". Com o trial da casa nascendo em
                     'trialing' (platform_admin.create_client), o teste antigo
                     `!== 'active'` apagava todo cliente em trial saudavel — e
                     tambem quem esta em past_due, que ainda opera na janela de
                     graca. Mesma regra que Suspender/Reativar usa abaixo. */
                  !OPERACIONAIS.has(c.status) ? styles.rowSuspended : ''
                }
              >
                <td>
                  <strong>{c.name}</strong>
                  <span className={styles.mutedSmall}> · {c.slug}</span>
                  <br />
                  <span className={styles.mutedSmall}>
                    {c.host ? `🌐 ${c.host}` : 'host compartilhado'}
                  </span>
                </td>
                <td>
                  <Badge tone={c.plan === 'enterprise' ? 'info' : 'neutral'} size="xs">
                    {c.package ?? c.plan}
                  </Badge>
                </td>
                <td className={styles.mono}>{c.ai_mode}</td>
                <td>
                  {c.brands.length > 0 ? (
                    c.brands.map((b) => (
                      <span key={b} className={styles.brandChip}>
                        {b}
                      </span>
                    ))
                  ) : (
                    <span className={styles.mutedSmall}>—</span>
                  )}
                </td>
                <td className={styles.mono}>{c.users}</td>
                <td className={styles.mono}>{usd(c.spend_month_usd)}</td>
                <td>
                  {/* O rotulo era binario (ativo|suspenso): um trial SAUDAVEL
                      aparecia "suspenso" com botao "Reativar" destacado — e
                      clicar promovia trialing→active, matando a expiracao do
                      trial. Medido ao vivo com um cliente recem-nascido do
                      signup. Cada estado agora tem nome e tom proprios. */}
                  <Badge tone={TOM_STATUS[c.status] ?? 'crit'} size="xs">
                    {ROTULO_STATUS[c.status] ?? c.status}
                  </Badge>
                </td>
                <td className={styles.actions}>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setPlanFor(c);
                      setPlanPkg(c.package ?? 'enterprise');
                      setMsg(null);
                    }}
                  >
                    Plano
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => openFeatures(c)}>
                    Features
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setAssignFor(c);
                      setAssignSlug('');
                      setMsg(null);
                    }}
                  >
                    + Marca
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={resetAdmin.isPending}
                    onClick={() => resetOne(c)}
                    title="Gera senha temporária nova e mostra o cartão de acesso AQUI — nenhum e-mail é enviado (envio automático em construção); repasse ao cliente por canal seguro"
                  >
                    Reenviar acesso
                  </Button>
                  {/* Suspender vale para quem esta OPERANDO (active, trialing,
                      past_due); Reativar so para suspended. Antes, "Reativar"
                      aparecia para um trial saudavel — e o clique gravava
                      status=active, apagando a natureza de trial da conta. */}
                  {OPERACIONAIS.has(c.status) ? (
                    <Button size="sm" variant="ghost" onClick={() => toggleStatus(c)}>
                      Suspender
                    </Button>
                  ) : c.status === 'suspended' ? (
                    <Button size="sm" variant="primary" onClick={() => toggleStatus(c)}>
                      Reativar
                    </Button>
                  ) : null}
                  <Button size="sm" variant="secondary" onClick={() => setHost(c)}>
                    Domínio
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setOffboardFor(c);
                      setOffboardConfirm('');
                      setMsg(null);
                    }}
                  >
                    Offboard
                  </Button>
                </td>
              </tr>
            ))}
            {clients.length === 0 && !clientsQ.isLoading && (
              <tr>
                <td colSpan={8} className={styles.muted}>
                  Nenhum cliente ainda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* ─── Analytics cruzado ───────────────────────────────────────── */}
      {/* ─── L4.3: o funil de receita (evento_funil, contrato K2) ─────── */}
      {tab === 'funil' && (
        <div>
          <h2 className={styles.sectionTitle}>
            Funil de receita — janela de {funilDias} dias
            <span className={styles.mutedSmall}>
              {' '}
              · quantos entraram, quantos converteram, quantos saíram
            </span>
          </h2>
          <div style={{ marginBottom: 'var(--sp-3)', display: 'flex', gap: 'var(--sp-2)' }}>
            {JANELAS.map((d) => (
              <Button
                key={d}
                size="sm"
                variant={funilDias === d ? 'primary' : 'secondary'}
                disabled={funilDias === d}
                onClick={() => setFunilDias(d)}
              >
                {d} dias
              </Button>
            ))}
          </div>

          {funilQ.isLoading && <p className={styles.muted}>Carregando…</p>}
          {funilQ.isError && (
            <p className={styles.muted}>Erro ao carregar: {describeError(funilQ.error)}</p>
          )}

          {funilQ.data && (
            <>
              <div className={styles.kpis}>
                <div className={styles.kpi}>
                  <span className={styles.kpiLabel}>Entraram</span>
                  <span className={styles.kpiValue}>{formatarNumero(funilQ.data.entraram)}</span>
                  <span className={styles.kpiHint}>cadastros iniciados</span>
                </div>
                <div className={styles.kpi}>
                  <span className={styles.kpiLabel}>Converteram</span>
                  <span className={styles.kpiValue}>{formatarNumero(funilQ.data.converteram)}</span>
                  <span className={styles.kpiHint}>clientes com pagamento confirmado</span>
                </div>
                <div className={styles.kpi}>
                  <span className={styles.kpiLabel}>Saíram</span>
                  <span className={styles.kpiValue}>{formatarNumero(funilQ.data.sairam)}</span>
                  <span className={styles.kpiHint}>clientes em churn</span>
                </div>
                <div className={styles.kpi}>
                  <span className={styles.kpiLabel}>Taxa de conversão</span>
                  <span className={styles.kpiValue}>
                    {/* `null` e 0% são coisas diferentes: sem ninguém entrando,
                        a taxa não é zero — é pergunta sem resposta. */}
                    {funilQ.data.taxa_conversao === null
                      ? '—'
                      : `${(funilQ.data.taxa_conversao * 100).toFixed(1)}%`}
                  </span>
                  <span className={styles.kpiHint}>
                    {funilQ.data.taxa_conversao === null ? 'ninguém entrou na janela' : ''}
                  </span>
                </div>
              </div>

              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Evento (contrato K2)</th>
                    <th>Ocorrências</th>
                    <th>Clientes distintos</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(funilQ.data.por_evento).map(([nome, c]) => (
                    <tr key={nome}>
                      <td>
                        <strong>{nome}</strong>
                      </td>
                      <td className={styles.mono}>{formatarNumero(c.linhas)}</td>
                      <td className={styles.mono}>{formatarNumero(c.clientes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className={styles.note}>
                {funilQ.data.escopo === 'casa'
                  ? 'Funil da plataforma inteira — inclusive os cadastros que ainda não viraram cliente (esses nascem sem dono, e só a casa os alcança).'
                  : 'Este acesso é de tenant: os números acima são apenas o ciclo de vida deste cliente. “Entraram” fica em zero porque o registro do cadastro não pertence a nenhum tenant.'}{' '}
                Clientes distintos, e não ocorrências, é o que conta em conversão e churn: uma
                assinatura mensal confirma pagamento todo mês e continua sendo um cliente só.
              </p>
            </>
          )}
        </div>
      )}

      {tab === 'analytics' && (
        <div>
          <div className={styles.kpis}>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>
                Pageviews ({analyticsQ.data?.window ?? '28d'})
              </span>
              <span className={styles.kpiValue}>
                {analyticsQ.data ? compact(analyticsQ.data.totals.pageviews) : '—'}
              </span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Cliques (GSC)</span>
              <span className={styles.kpiValue}>
                {analyticsQ.data ? compact(analyticsQ.data.totals.clicks) : '—'}
              </span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Impressões (GSC)</span>
              <span className={styles.kpiValue}>
                {analyticsQ.data ? compact(analyticsQ.data.totals.impressions) : '—'}
              </span>
            </div>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Matérias</th>
                <th>Pageviews</th>
                <th>Cliques</th>
                <th>Impressões</th>
                <th>CTR</th>
              </tr>
            </thead>
            <tbody>
              {(analyticsQ.data?.by_client ?? []).map((c) => (
                <tr key={c.client_slug}>
                  <td>
                    <strong>{c.client_name}</strong>
                    <span className={styles.mutedSmall}> · {c.client_slug}</span>
                  </td>
                  <td className={styles.mono}>{c.articles}</td>
                  <td className={styles.mono}>{formatarNumero(c.pageviews)}</td>
                  <td className={styles.mono}>{formatarNumero(c.clicks)}</td>
                  <td className={styles.mono}>{formatarNumero(c.impressions)}</td>
                  <td className={styles.mono}>{(c.ctr * 100).toFixed(2)}%</td>
                </tr>
              ))}
              {(analyticsQ.data?.by_client ?? []).length === 0 && (
                <tr>
                  <td colSpan={6} className={styles.muted}>
                    Sem métricas coletadas ainda (article_metrics vazio para a janela).
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <p className={styles.note}>
            Audiência agregada por cliente, cruzando todas as marcas — a plataforma coleta analytics
            de todos para melhoria contínua.
          </p>
        </div>
      )}

      {/* ─── B-47: uso real de features (audit_log) ──────────────────── */}
      {tab === 'features' && (
        <div>
          <h2 className={styles.sectionTitle}>
            Uso real por área — janela de {usageDays} dias
            <span className={styles.mutedSmall}>
              {' '}
              · minerado do log de auditoria; decide o que completar ou remover
            </span>
          </h2>
          <div style={{ marginBottom: 'var(--sp-3)', display: 'flex', gap: 'var(--sp-2)' }}>
            {([30, 90] as const).map((d) => (
              <Button
                key={d}
                size="sm"
                variant={usageDays === d ? 'primary' : 'secondary'}
                disabled={usageDays === d}
                onClick={() => setUsageDays(d)}
              >
                {d} dias
              </Button>
            ))}
          </div>

          {featureUsageQ.isLoading && <p className={styles.muted}>Carregando…</p>}
          {featureUsageQ.isError && (
            <p className={styles.muted}>Erro ao carregar: {describeError(featureUsageQ.error)}</p>
          )}

          {featureUsageQ.data && (
            <>
              {featureUsageQ.data.silent_areas.length > 0 && (
                <div className={styles.card} style={{ marginBottom: 'var(--sp-4)' }}>
                  <strong>
                    Sem NENHUM uso na janela ({featureUsageQ.data.silent_areas.length}):
                  </strong>{' '}
                  {featureUsageQ.data.silent_areas.map((s) => s.label).join(' · ')}
                  <div className={styles.mutedSmall}>
                    Candidatas a completar ou remover — a ausência é o dado.
                  </div>
                </div>
              )}
              <div className={styles.scrollX}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Área</th>
                      <th>Eventos</th>
                      <th>Atores</th>
                      <th>Clientes</th>
                      <th>Último evento</th>
                      <th>Ação mais comum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featureUsageQ.data.used.map((u) => (
                      <tr key={u.area}>
                        <td>
                          <strong>{u.label}</strong>
                        </td>
                        <td className={styles.mono}>{compact(u.events)}</td>
                        <td className={styles.mono}>{u.actors}</td>
                        <td className={styles.mono}>{u.clients}</td>
                        <td className={styles.mono}>
                          {u.last_event_at
                            ? formatarDataHora(u.last_event_at, {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                        </td>
                        <td className={styles.mutedSmall}>
                          {u.actions[0]
                            ? `${u.actions[0].action} (${compact(u.actions[0].events)})`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                    {featureUsageQ.data.used.length === 0 && (
                      <tr>
                        <td colSpan={6} className={styles.muted}>
                          Nenhum evento de auditoria na janela — o log começa a contar a partir do
                          uso.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── Consumo (usage trend + billing) ─────────────────────────── */}
      {tab === 'usage' && (
        <div>
          <h2 className={styles.sectionTitle}>
            Consumo de IA por cliente — {usageMonths.length} meses
          </h2>
          <div className={styles.scrollX}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Cliente</th>
                  {usageMonths.map((m) => (
                    <th key={m}>{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(usageQ.data?.by_client ?? []).map((c) => (
                  <tr key={c.client_slug}>
                    <td>
                      {c.client_slug === '__pool__' ? (
                        <span className={styles.mutedSmall}>{c.client_name}</span>
                      ) : (
                        <strong>{c.client_name}</strong>
                      )}
                    </td>
                    {usageMonths.map((m) => (
                      <td key={m} className={styles.mono}>
                        {c.points[m] ? usd(c.points[m]) : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
                {(usageQ.data?.by_client ?? []).length === 0 && (
                  <tr>
                    <td colSpan={usageMonths.length + 1} className={styles.muted}>
                      Sem consumo registrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <h2 className={styles.sectionTitle}>
            Billing — {billingQ.data?.period ?? 'mês corrente'}
            {billingQ.data && (
              <span className={styles.mutedSmall}> · total {usd(billingQ.data.total_usd)}</span>
            )}
          </h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Provider</th>
                <th>Gasto (USD)</th>
              </tr>
            </thead>
            <tbody>
              {(billingQ.data?.items ?? []).map((i) => (
                <tr key={`${i.client_slug}-${i.provider}`}>
                  <td>
                    {i.client_id ? (
                      i.client_name
                    ) : (
                      <span className={styles.mutedSmall}>{i.client_name}</span>
                    )}
                  </td>
                  <td className={styles.mono}>{i.provider}</td>
                  <td className={styles.mono}>{usd(i.spent_usd)}</td>
                </tr>
              ))}
              {(billingQ.data?.items ?? []).length === 0 && (
                <tr>
                  <td colSpan={3} className={styles.muted}>
                    Sem consumo registrado no período.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── SERP (global) ───────────────────────────────────────────── */}
      {tab === 'serp' && (
        <div>
          <div className={styles.kpis}>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Provedor ativo</span>
              <span className={styles.kpiValue}>{serpQ.data?.active_provider ?? '—'}</span>
              <span className={styles.kpiHint}>
                disponíveis: {(serpQ.data?.providers ?? []).join(', ')}
              </span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiLabel}>Chave</span>
              <span className={styles.kpiValue}>{serpQ.data?.key.present ? '✓' : '—'}</span>
              <span className={styles.kpiHint}>
                {serpQ.data?.key.present
                  ? `${serpQ.data.key.masked} (${serpQ.data.key.source})`
                  : 'sem chave configurada'}
              </span>
            </div>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Provider</th>
                <th>Consumo (USD)</th>
              </tr>
            </thead>
            <tbody>
              {(serpQ.data?.consumption ?? []).map((c) => (
                <tr key={c.provider}>
                  <td className={styles.mono}>{c.provider}</td>
                  <td className={styles.mono}>{usd(c.spent_usd)}</td>
                </tr>
              ))}
              {(serpQ.data?.consumption ?? []).length === 0 && (
                <tr>
                  <td colSpan={2} className={styles.muted}>
                    Sem consumo SERP registrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <p className={styles.note}>
            SERP é a chave única da casa (não é por-cliente). {serpQ.data?.note}
          </p>
        </div>
      )}

      {/* ─── Vigias (global) ─────────────────────────────────────────── */}
      {tab === 'vigias' && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Vigias</th>
              <th>Eventos</th>
            </tr>
          </thead>
          <tbody>
            {(vigiasQ.data?.by_client ?? []).map((c) => (
              <tr key={c.client_slug}>
                <td>
                  {c.client_slug === '__pool__' ? (
                    <span className={styles.mutedSmall}>{c.client_name}</span>
                  ) : (
                    <strong>{c.client_name}</strong>
                  )}
                </td>
                <td>
                  {c.vigias.map((v) => (
                    <span key={v.slug} className={styles.brandChip} title={v.nome}>
                      {v.slug}
                    </span>
                  ))}
                </td>
                <td className={styles.mono}>{c.events}</td>
              </tr>
            ))}
            {(vigiasQ.data?.by_client ?? []).length === 0 && (
              <tr>
                <td colSpan={3} className={styles.muted}>
                  Nenhuma vigia.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* ─── Modais ──────────────────────────────────────────────────── */}
      {creating && (
        <div className={styles.modalBackdrop} onClick={() => setCreating(false)}>
          <form
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitCreate}
          >
            <h2 className={styles.modalTitle}>Novo cliente</h2>
            <label className={styles.field}>
              <span>Slug (a-z, 0-9, hífen)</span>
              <input
                required
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </label>
            <label className={styles.field}>
              <span>Nome</span>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <div className={styles.fieldRow}>
              <label className={styles.field}>
                <span>Pacote (plano)</span>
                <select
                  value={form.pkg}
                  onChange={(e) => setForm({ ...form, pkg: e.target.value })}
                >
                  {(packagesQ.data?.packages ?? []).map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Chave de IA</span>
                <select
                  value={form.ai_mode}
                  onChange={(e) => setForm({ ...form, ai_mode: e.target.value })}
                >
                  <option value="pool">pool (da casa, metrado)</option>
                  <option value="own">own (própria)</option>
                </select>
              </label>
            </div>
            {(() => {
              const p = packagesQ.data?.packages.find((x) => x.key === form.pkg);
              if (!p) return null;
              const on = Object.entries(p.features)
                .filter(([, v]) => v)
                .map(([k]) => k);
              return (
                <div className={styles.pkgSummary}>
                  <span className={styles.mutedSmall}>
                    {/* O preço fica aqui porque é aqui que a decisão é tomada:
                        quem cria o tenant é quem vai emitir a fatura à mão, e
                        ter de lembrar de cor qual pacote é 499 e qual é 999 é
                        como se cobra o número errado. A moeda vem junto — um
                        "499" solto numa tela em português se lê como real. */}
                    <strong>
                      {p.moeda} {p.price_month}
                    </strong>
                    /mês · plano <strong>{p.plan}</strong> · {on.length} de{' '}
                    {Object.keys(p.features).length} features ligadas
                    {!p.publico && ' · fora da vitrine'}
                  </span>
                  <div className={styles.pkgChips}>
                    {on.map((f) => (
                      <span key={f} className={styles.brandChip}>
                        {f}
                      </span>
                    ))}
                  </div>
                  <span className={styles.mutedSmall}>
                    Quotas:{' '}
                    {Object.entries(p.quotas)
                      .map(([k, v]) => `${k}=${v === -1 ? '∞' : v}`)
                      .join(' · ')}
                  </span>
                </div>
              );
            })()}
            <label className={styles.field}>
              <span>E-mail do admin (opcional — cria o 1º acesso)</span>
              <input
                type="email"
                value={form.admin_email}
                onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
              />
            </label>
            {form.admin_email.trim() && (
              <label className={styles.field}>
                <span>Nome do admin</span>
                <input
                  value={form.admin_nome}
                  onChange={(e) => setForm({ ...form, admin_nome: e.target.value })}
                />
              </label>
            )}
            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setCreating(false)}>
                Cancelar
              </Button>
              <Button type="submit" loading={createClient.isPending}>
                Criar
              </Button>
            </div>
          </form>
        </div>
      )}

      {created && (
        <div className={styles.modalBackdrop} onClick={() => setCreated(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Cartão de Acesso — {created.slug}</h2>
            {created.temp_password ? (
              <>
                <p className={styles.muted}>
                  Entregue estes dados ao cliente. A senha temporária é mostrada
                  <strong> uma única vez</strong> — o admin troca no primeiro acesso.
                </p>
                <div className={styles.card}>
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>URL de acesso</span>
                    <code className={styles.cardValue}>{appUrl}</code>
                    <button
                      type="button"
                      className={styles.copyBtn}
                      onClick={() => copy('url', appUrl)}
                    >
                      {copied === 'url' ? '✓' : 'Copiar'}
                    </button>
                  </div>
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Login (e-mail)</span>
                    <code className={styles.cardValue}>{created.admin_email}</code>
                    <button
                      type="button"
                      className={styles.copyBtn}
                      onClick={() => copy('email', created.admin_email ?? '')}
                    >
                      {copied === 'email' ? '✓' : 'Copiar'}
                    </button>
                  </div>
                  <div className={styles.cardRow}>
                    <span className={styles.cardLabel}>Senha temporária</span>
                    <code className={styles.cardValue}>{created.temp_password}</code>
                    <button
                      type="button"
                      className={styles.copyBtn}
                      onClick={() => copy('pw', created.temp_password ?? '')}
                    >
                      {copied === 'pw' ? '✓' : 'Copiar'}
                    </button>
                  </div>
                </div>
                <ol className={styles.steps}>
                  <li>Abra a URL de acesso.</li>
                  <li>
                    Entre com o <strong>e-mail</strong> e a senha temporária.
                  </li>
                  <li>Defina a nova senha no primeiro acesso.</li>
                </ol>
                <div className={styles.modalActions}>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      copy(
                        'all',
                        `URL: ${appUrl}\nLogin: ${created.admin_email}\nSenha temporária: ${created.temp_password}`,
                      )
                    }
                  >
                    {copied === 'all' ? '✓ Copiado' : 'Copiar tudo'}
                  </Button>
                  <Button onClick={() => setCreated(null)}>Fechei, anotei</Button>
                </div>
              </>
            ) : (
              <>
                <p className={styles.muted}>
                  Cliente sem admin criado. Use <strong>Reenviar acesso</strong> na linha do cliente
                  após criar um usuário admin, ou atribua marcas.
                </p>
                <div className={styles.modalActions}>
                  <Button onClick={() => setCreated(null)}>Fechar</Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {assignFor && (
        <div className={styles.modalBackdrop} onClick={() => setAssignFor(null)}>
          <form
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitAssign}
          >
            <h2 className={styles.modalTitle}>Atribuir marca a {assignFor.name}</h2>
            <p className={styles.muted}>
              A marca (wp_site) deve existir em Configurações → Sites; aqui ela muda de dono.
            </p>
            <label className={styles.field}>
              <span>Slug do site</span>
              <input required value={assignSlug} onChange={(e) => setAssignSlug(e.target.value)} />
            </label>
            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setAssignFor(null)}>
                Cancelar
              </Button>
              <Button type="submit" loading={assignSite.isPending}>
                Atribuir
              </Button>
            </div>
          </form>
        </div>
      )}

      {offboardFor && (
        <div className={styles.modalBackdrop} onClick={() => setOffboardFor(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Offboarding — {offboardFor.name}</h2>
            <p className={styles.muted}>
              Status atual: <strong>{offboardFor.status}</strong>. Exporte antes de excluir (LGPD).
              A exclusão é em 2 passos: marcar para exclusão e depois purgar.
            </p>
            <div className={styles.modalActions} style={{ justifyContent: 'flex-start' }}>
              <Button
                variant="secondary"
                loading={exportClient.isPending}
                onClick={() => doExport(offboardFor)}
              >
                Exportar dados (JSON)
              </Button>
            </div>
            <label className={styles.field}>
              <span>
                Confirme digitando o slug: <code>{offboardFor.slug}</code>
              </span>
              <input value={offboardConfirm} onChange={(e) => setOffboardConfirm(e.target.value)} />
            </label>
            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setOffboardFor(null)}>
                Cancelar
              </Button>
              <Button
                variant="secondary"
                disabled={offboardConfirm !== offboardFor.slug}
                loading={deprovision.isPending}
                onClick={() => doDeprovision('soft_delete')}
              >
                Marcar p/ exclusão
              </Button>
              {offboardFor.status === 'deleting' && (
                <Button
                  variant="danger"
                  disabled={offboardConfirm !== offboardFor.slug}
                  loading={purge.isPending}
                  onClick={doPurge}
                >
                  PURGAR definitivo
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {planFor && (
        <div className={styles.modalBackdrop} onClick={() => setPlanFor(null)}>
          <form className={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={submitPlan}>
            <h2 className={styles.modalTitle}>Mudar plano — {planFor.name}</h2>
            <p className={styles.muted}>
              Reaplica features e quotas do pacote. Downgrade não apaga dados — apenas desliga
              (gateia) features; voltam no upgrade.
            </p>
            <label className={styles.field}>
              <span>Pacote</span>
              <select value={planPkg} onChange={(e) => setPlanPkg(e.target.value)}>
                {(packagesQ.data?.packages ?? []).map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            {(() => {
              const p = packagesQ.data?.packages.find((x) => x.key === planPkg);
              if (!p) return null;
              const on = Object.entries(p.features)
                .filter(([, v]) => v)
                .map(([k]) => k);
              return (
                <div className={styles.pkgSummary}>
                  <span className={styles.mutedSmall}>
                    Plano <strong>{p.plan}</strong> · {on.length} features · quotas{' '}
                    {Object.entries(p.quotas)
                      .map(([k, v]) => `${k}=${v === -1 ? '∞' : v}`)
                      .join(' · ')}
                  </span>
                </div>
              );
            })()}
            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setPlanFor(null)}>
                Cancelar
              </Button>
              <Button type="submit" loading={updateClient.isPending}>
                Aplicar plano
              </Button>
            </div>
          </form>
        </div>
      )}

      {featuresFor && (
        <div className={styles.modalBackdrop} onClick={() => setFeaturesFor(null)}>
          <form
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitFeatures}
          >
            <h2 className={styles.modalTitle}>Features — {featuresFor.name}</h2>
            <p className={styles.muted}>
              JSON de entitlements. Regra: ausente = liberado; <code>false</code> explícito
              bloqueia. Ex.: {'{"vigias": false}'}
            </p>
            <textarea
              className={styles.jsonArea}
              rows={8}
              value={featuresText}
              onChange={(e) => setFeaturesText(e.target.value)}
            />
            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={() => setFeaturesFor(null)}>
                Cancelar
              </Button>
              <Button type="submit" loading={updateClient.isPending}>
                Salvar
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
